/**
 * Red Star — the Lesaffre wine-yeast range sold in the United States, and the
 * five Premier strains a brewer actually buys: champagne yeast for bottle
 * conditioning and seltzer, and the cider/mead workhorses.
 *
 * Red Star publishes no product page of its own for these. `redstaryeast.com`
 * is the baking brand and lists only baker's yeast; Fermentis sells its own
 * SafŒno line rather than Red Star; and `lesaffreyeast.com` serves no usable
 * TLS certificate. The figures below come off the Lesaffre technical data
 * sheets themselves — each headed "Fermentis, a Division of Lesaffre Yeast
 * Corporation" — which only a distributor hosts, so every entry is written
 * without a `url` rather than pointing at a shop. The Côte des Blancs
 * temperature band is the one Red Star's own older sheet for the strain gives
 * (17–30 °C); the current sheet publishes only a 12–14 °C floor.
 *
 * All five are `representative`: the sheets give temperature, alcohol
 * tolerance and dosage but never attenuation or flocculation. Attenuation is
 * therefore the band a wine strain fermenting to dryness implies, narrowed for
 * Côte des Blancs because its sheet says the fermentation can be arrested cool
 * with sugar still in the must. Flocculation is class-typical. STA1 is `null`
 * throughout — Lesaffre publishes no result for the wine range.
 *
 * Every strain was renamed when Red Star tidied the range under the "Premier"
 * banner; the old names are still what shops and recipes say, so they are in
 * both the tags and the descriptions. Type is `ale` and dosage is the
 * published 20–30 g/hL.
 */

import type { Yeast } from '../../schema/yeast';

/** Red Star wine yeast. @see Yeast */
export const redStarYeasts: readonly Yeast[] = [
  {
    id: 'red-star-premier-blanc',
    lab: 'Red Star',
    productCode: 'Premier Blanc',
    name: 'Premier Blanc',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 10, max: 35 },
    flocculation: 'medium',
    alcoholTolerancePercent: 18,
    sta1: null,
    description:
      'Sold as Pasteur Champagne until Red Star tidied the range: a bayanus strain that ferments hard and bone dry to 18 % and shrugs off sulphite, cold and fructose-heavy musts. Brewers keep it for bottle-conditioning big beers, for hard seltzer, and for rescuing a barleywine that has stopped moving — its profile is neutral enough to leave the base alone.',
    tags: [
      'wine',
      'champagne',
      'sparkling',
      'seltzer',
      'high-gravity',
      'bottle-conditioning',
      'neutral',
      'dry-finish',
      'stuck-fermentation',
      'pasteur-champagne',
    ],
    dosageGramsPerHl: { min: 20, max: 30 },
    provenance: 'representative',
  },
  {
    id: 'red-star-premier-cuvee',
    lab: 'Red Star',
    productCode: 'Premier Cuvée',
    name: 'Premier Cuvée',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 10, max: 35 },
    flocculation: 'medium',
    alcoholTolerancePercent: 16,
    sta1: null,
    description:
      'The old Prise de Mousse, and still the fastest and most neutral strain in the range: it starts quickly, ferments to dryness anywhere between 10 and 35 °C and holds to 16 % alcohol. The default pitch for priming a strong ale in bottle, for a seltzer base, and for restarting a fermentation that has quit.',
    tags: [
      'wine',
      'champagne',
      'sparkling',
      'seltzer',
      'bottle-conditioning',
      'neutral',
      'fast',
      'dry-finish',
      'stuck-fermentation',
      'prise-de-mousse',
    ],
    dosageGramsPerHl: { min: 20, max: 30 },
    provenance: 'representative',
  },
  {
    id: 'red-star-premier-classique',
    lab: 'Red Star',
    productCode: 'Premier Classique',
    name: 'Premier Classique',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 93, max: 100 },
    temperatureC: { min: 14, max: 35 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: null,
    description:
      'Montrachet under its newer name — the first wine strain the University of California at Davis selected, and the one to reach for when there is no temperature control. Medium-paced, good to 15 % alcohol, tolerant of heavy sulphite, and very low in volatile acidity, so a cider or mead keeps its own fruit character.',
    tags: [
      'wine',
      'cider',
      'mead',
      'neutral',
      'dry-finish',
      'red-wine',
      'white-wine',
      'no-temperature-control',
      'montrachet',
    ],
    dosageGramsPerHl: { min: 20, max: 30 },
    provenance: 'representative',
  },
  {
    id: 'red-star-premier-rouge',
    lab: 'Red Star',
    productCode: 'Premier Rouge',
    name: 'Premier Rouge',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 93, max: 100 },
    temperatureC: { min: 17, max: 30 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: null,
    description:
      'Formerly Pasteur Red: a Pasteur Institute isolate picked for full-bodied reds, where it pulls polyphenols hard and builds structure rather than simple fruit. It wants a controlled 17–30 °C and tolerates 15 % alcohol, which makes it a sane pitch for a dark fruit wine or a heavy melomel.',
    tags: [
      'wine',
      'red-wine',
      'fruit-wine',
      'melomel',
      'mead',
      'structure',
      'dry-finish',
      'pasteur-red',
    ],
    dosageGramsPerHl: { min: 20, max: 30 },
    provenance: 'representative',
  },
  {
    id: 'red-star-cote-des-blancs',
    lab: 'Red Star',
    productCode: 'Côte des Blancs',
    name: 'Premier Côte des Blancs',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 85, max: 95 },
    temperatureC: { min: 17, max: 30 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 14,
    sta1: null,
    description:
      'The Epernay 2 strain, and the Red Star wine yeast a cider or mead maker actually wants: slow, aromatic, and easy to arrest cool if you would rather leave sugar in the glass than ferment it out. Alcohol tolerance is the lowest of the range at 14 %, and it sulks below about 13 °C.',
    tags: [
      'wine',
      'cider',
      'mead',
      'white-wine',
      'fruity',
      'aromatic',
      'residual-sweetness',
      'slow',
      'epernay-2',
    ],
    dosageGramsPerHl: { min: 20, max: 30 },
    provenance: 'representative',
  },
];
